<template>
  <c-sidesheet id="type-modal" :active="displayModal" @close="close">
    <m-confirm ref="confirm" mode="danger" />

    <div v-show="!isLoadingData" class="modal-frame">
      <div class="modal-wrapper">
        <shadow-scroll-container ref="track" class="modal-track" :with-top-scroller="true">
          <div class="modal-frame__header">
            <h2 class="modal-frame__title">Новая проводка</h2>
          </div>

          <div class="form-group">
            <c-switcher
              v-model="transactionTypes.selectedType"
              class="h-switcher"
              type="group"
              :items="transactionTypes.types"
            />
          </div>

          <div class="form-group">
            <div class="form-group__column">
              <c-select-2
                v-model="transactionTypes.selectedItem"
                class="modal-frame__transaction-type"
                type="select"
                floating-label="Тип проводки"
                :clearable="true"
                :searchable="true"
                :items="transactionTypes.items"
                :use-custom-colors="true"
                :required="isSubmitted && transactionTypesError"
                :required-message="isSubmitted && transactionTypesError"
                :required-mark="true"
              />
              <c-button
                v-if="transactionTypes.selectedItem && transactionTypes.selectedItem.description"
                v-b-popover.html="{
                  container: '#app',
                  trigger: 'click blur',
                  boundary: 'window',
                  content: transactionTypes.selectedItem.description,
                  placement: 'top',
                }"
                class="form-group__item-description form-group__item-description--desktop"
                size="iconic"
                icon="info-new"
              />
              <c-button
                v-if="transactionTypes.selectedItem && transactionTypes.selectedItem.description"
                v-b-popover.html="{
                  container: '#app',
                  trigger: 'click blur',
                  boundary: 'window',
                  content: transactionTypes.selectedItem.description,
                  placement: 'top',
                }"
                class="form-group__item-description form-group__item-description--mobile"
                icon="info-new"
                label="Показать описание"
              />
            </div>
          </div>

          <div v-if="balances.worker_id" class="form-group">
            <div class="form-group__column">
              <div class="form-group__item-balances">
                <div class="form-group__item-balances-item">
                  <div class="form-group__item-balances-item-header">
                    <div class="form-group__item-balances-item-number" :class="{'positive': balances.personal_amount > 0, 'negative': balances.personal_amount < 0 }">
                      {{ formatNumber(balances.personal_amount) }} {{currencyLabel}}
                    </div>
                    <c-button
                      v-b-popover.html="{
                        container: '#app',
                        trigger: 'hover click blur',
                        boundary: 'window',
                        content: filialsWithBalances,
                        placement: 'top',
                      }"
                      class="form-group__balances-description"
                      size="iconic"
                      icon="info-new"
                    />
                  </div>
                  <div class="form-group__item-balances-item-name">Личный счет</div>
                </div>
                <div class="form-group__item-balances-item">
                  <div class="form-group__item-balances-item-header">
                    <div class="form-group__item-balances-item-number" :class="{'positive': balances.deposit_amount > 0, 'negative': balances.deposit_amount < 0 }">
                      {{ formatNumber(balances.deposit_amount) }} {{currencyLabel}}
                    </div>
                  </div>
                  <div class="form-group__item-balances-item-name">Депозит</div>
                </div>
                <div class="form-group__item-balances-item">
                  <div class="form-group__item-balances-item-header">
                    <div class="form-group__item-balances-item-number" :class="{'positive': balances.accountable_amount > 0, 'negative': balances.accountable_amount < 0 }">
                      {{ formatNumber(balances.accountable_amount) }} {{currencyLabel}}
                    </div>
                  </div>
                  <div class="form-group__item-balances-item-name">Подотчет</div>
                </div>
              </div>
            </div>
          </div>

          <div class="form-group">
            <div class="form-group__column form-group__column--2">
              <c-input
                v-if="!transactionTypes.selectedItem"
                v-model="filials.selectedFilial"
                class="modal-frame__transaction-type"
                type="select"
                floating-label="Филиал"
                :disabled="!transactionTypes.selectedItem"
              />
              <c-select-2
                v-else
                v-model="filials.selectedFilial"
                type="select"
                floating-label="Филиал"
                :clearable="!isFilialRequired"
                :searchable="true"
                :items="filials.allFilials"
                :use-custom-colors="true"
                :required="isSubmitted && filialError"
                :required-message="isSubmitted && filialError"
                :required-mark="isFilialRequired"
                :disabled="!transactionTypes.selectedItem"
              />
            </div>
            <div class="form-group__column form-group__column--2">
              <c-input
                v-if="!transactionTypes.selectedItem"
                v-model="posts.selectedPost"
                class="modal-frame__transaction-type"
                type="select"
                floating-label="Должность"
                :disabled="!transactionTypes.selectedItem"
              />
              <c-select-2
                v-else
                v-model="posts.selectedPost"
                class="modal-frame__filial"
                type="select"
                floating-label="Должность"
                :clearable="!isPostCodeRequired"
                :searchable="true"
                :items="posts.allPosts"
                :use-custom-colors="true"
                :required="isSubmitted && postError"
                :required-message="isSubmitted && postError"
                :required-mark="isPostCodeRequired"
                :disabled="!transactionTypes.selectedItem"
              />
            </div>
          </div>

          <div class="form-group">
            <div class="form-group__column">
              <c-input
                v-model="transactionReasons.selectedReason"
                class="modal-frame__transaction-type"
                type="select"
                :floating-label="transactionReason"
                :clearable="true"
                :searchable="true"
                :items="transactionReasons.items"
                :use-custom-colors="true"
                :required="isSubmitted && transactionReasonsError"
                :required-condition="isSubmitted && transactionReasonsError"
                :required-mark="true"
                :class="{ 'is-invalid': isSubmitted && transactionReasonsError }"
                :disabled="!transactionTypes.selectedItem"
              />
              <c-button
                v-if="transactionReasons.selectedReason && transactionReasons.selectedReason.subLabel"
                v-b-popover.html="{
                  container: '#app',
                  trigger: 'click blur',
                  boundary: 'window',
                  content: transactionReasons.selectedReason.subLabel,
                  placement: 'top',
                }"
                class="form-group__item-description form-group__item-description--desktop"
                size="iconic"
                icon="info-new"
              />
              <c-button
                v-if="transactionReasons.selectedReason && transactionReasons.selectedReason.subLabel"
                v-b-popover.html="{
                  container: '#app',
                  trigger: 'click blur',
                  boundary: 'window',
                  content: transactionReasons.selectedReason.subLabel,
                  placement: 'top',
                }"
                class="form-group__item-description form-group__item-description--mobile"
                icon="info-new"
                label="Показать описание"
              />
            </div>
          </div>

          <div class="form-group">
            <div class="form-group__column form-group__column--3">
              <c-input
                v-model="transactionAmount"
                type="price-number"
                label="Сумма, ฿"
                placeholder="0"
                :max-length="12"
                :clearable="true"
                :required="isSubmitted && transactionAmountError"
                :required-message="isSubmitted && transactionAmountError"
                :input-text-condition="isSubmitted && transactionAmountError"
                :required-mark="true"
              />
            </div>
            <div class="form-group__column form-group__column--3">
              <c-input
                ref="dateInput"
                v-model="transactionDate.value"
                label="Дата учетная"
                :editable="true"
                :clearable="true"
                type="date"
                format="DD.MM.YYYY, dddd"
                icon="calendar"
                placeholder="00.00.0000"
                :required="isSubmitted && transactionDate.error"
                :required-message="isSubmitted && transactionDate.error"
                :required-mark="true"
                :icon-right="true"
                @invalidDate="sendErrorDateMessage"
              />
            </div>
          </div>

          <div class="form-group">
            <div class="form-group__column">
              <c-select-2
                v-model="workersList.selectedWorker"
                type="select"
                floating-label="Кто принял решение"
                :clearable="true"
                :searchable="true"
                :items="workersList.items"
              />
            </div>
          </div>

          <div class="form-group">
            <div class="form-group__column">
              <c-select-2
                v-model="companies.selectedCompany"
                class="modal-frame__transaction-type"
                type="select"
                floating-label="Юр. лицо"
                :clearable="true"
                :searchable="true"
                :items="companies.items"
                :use-custom-colors="true"
                :required="isSubmitted && companiesError"
                :required-message="isSubmitted && companiesError"
                :required-mark="true"
              />
            </div>
          </div>

          <div class="form-group">
            <div class="form-group__column">
              <c-input
                ref="comment2"
                v-model="comment"
                type="textarea"
                floating-label="Комментарий"
                :clearable="true"
                max-length="255"
              />
            </div>
          </div>

          <div class="modal-frame__actions">
            <c-button class="modal-frame__action" label="Отменить" size="large" @click="close" />
            <c-button class="modal-frame__action" label="Сохранить" size="large" color="primary" :disabled="blocked" @click="submit" />
          </div>
        </shadow-scroll-container>
      </div>
      <div class="modal-actions">
        <c-button size="large" label="Отменить" @click="close" />
        <c-button label="Сохранить" color="primary" size="large" @click="submit" />
      </div>
    </div>

    <c-loader
      v-if="isLoadingData"
      label="Данные загружаются"
      :active="isLoadingData"
      :height="'400'"
    />
  </c-sidesheet>
</template>

<script>
import { mapState } from "vuex";
import CInput from "@/components/controls2/Input";
import CTextarea from "@/components/textarea";
import CButton from "@/components/controls2/Button";
import CCheckbox from "@/components/checkbox";
import CSidesheet from "@/components/sidesheet";
import MConfirm from "@/components/modals/confirm-modal";
import FileLoader from "@/components/file-loader";
import TopButton from "@/components/top-button";
import ShadowScrollContainer from "@/components/shadow-scroll-container";
import Input2 from "@/components/controls2/Input";
import Switcher from "@/components/controls2/Switcher";
import Select2 from "@/components/controls2/Select";
import Loader from "@/components/loader";

export default {
  name: "new-transaction-modal",
  components: {
    CInput,
    CTextarea,
    CButton,
    CCheckbox,
    CSidesheet,
    MConfirm,
    FileLoader,
    TopButton,
    ShadowScrollContainer,
    "c-input-2": Input2,
    "c-switcher": Switcher,
    "c-select-2": Select2,
    "c-loader": Loader,
  },
  data() {
    return {
      currencyLabel: '฿',
      isSubmitted: false,
      displayModal: false,
      transactionAmount: '',
      comment: '',
      submitIsDisabled: false,
      isEditableMode: false,
      isLoadingData: false,
      blocked: false,
      balances: {},
      selectedTransactionTypeFromStore: null,

      transactionDate: {
        value: '',
        error: '',
      },

      worker: {
        id: 0,
        filial: '',
        filial_id: 0,
        postCodes: [],
      },

      filials: {
        selectedFilial: '',
        allFilials: [],
      },

      posts: {
        selectedPost: '',
        allPosts: [],
      },

      transactionTypes: {
        types: [
          { label: 'Списание', value: 'withdraw' },
          { label: 'Начисление', value: 'deposit', borderColor: 'green' },
        ],
        selectedType: "withdraw",
        items: [],
        selectedItem: '',
      },

      transactionReasons: {
        items: [],
        selectedReason: ''
      },

      workersList: {
        items: [],
        selectedWorker: '',
      },

      companies: {
        items: [],
        selectedCompany: '',
      }
    };
  },
  async created() {
    this.getCurrencyLabel()
  },
  computed: {
    ...mapState({
      userId: (state) => state.personalEmployee.transactions.userId,
      transactionTypesFromStore: (state) => state.personalEmployee.transactions.transactionTypes,
      transactionTypesForLsFromStore: (state) => state.personalEmployee.transactions.transactionTypesForLs,
      transactionReasonsFromStore: (state) => state.personalEmployee.transactions.transactionReasons,
      workersListFromStore: (state) => state.personalEmployee.transactions.workersList,
      currentUserWorker: (state) => state.personalEmployee.transactions.currentUserWorker,
      companiesFromStore: (state) => state.personalEmployee.transactions.companies,
      employeeInfo: (state) => state.personalEmployee.transactions.employeeInfo,
      allTransactionTypesFromStore: (state) => state.personalEmployee.transactions.allTransactionTypes,
    }),
    transactionTypesError() {
      return !this.transactionTypes.selectedItem && "Обязательное поле";
    },
    filialError() {
      return (this.isFilialRequired && !this.filials.selectedFilial) && "Обязательное поле";
    },
    postError() {
      return (this.isPostCodeRequired && !this.posts.selectedPost) && "Обязательное поле";
    },
    transactionReasonsError() {
      return !this.transactionReasons.selectedReason && "Обязательное поле";
    },
    transactionAmountError() {
      //      return !this.transactionAmount && "Обязательное поле" || Number(this.transactionAmount) === 0 && "Введите сумму больше нуля";
      return !this.transactionAmount && "Обязательное поле" || Number(this.transactionAmount) === 0 && "Введите сумму больше нуля" || Number(this.transactionAmount) > 9999999.99 && "Не больше 9999999.99" ;

    },
    companiesError() {
      return !this.companies.selectedCompany && "Обязательное поле";
    },
    transactionDateError() {
      return !this.transactionDate.value && "Обязательное поле";
    },
    transactionReason() {
      return this.transactionTypes.selectedItem && this.transactionTypes.selectedItem.dropdown_name ? this.transactionTypes.selectedItem.dropdown_name : 'Причина проводки';
    },
    filialsWithBalances() {
      const array = [];

      this.filials.allFilials.forEach(filial => {
        if (filial.amount !== null || (this.employeeInfo.filial && filial.value==this.employeeInfo.filial.id)) {
          if (!filial.amount) filial.amount=0;
          array.push(`
            <div style='display: flex; align-items: center; justify-content: space-between;'>
              <span>${filial.label}</span>
              <span style='margin-left: 10px; color:${filial.amount > 0 ? '#12C971;' : filial.amount < 0 ? '#FF238D;' : '#8E9CBB;' }'>${filial.amount} ฿</span>
            </div>`);
        }
      });

      array.push('<button class="tablet close-popover">Ok</button>');

      return array.join('<br/>');
    },
    isPostCodeRequired() {
      return this.selectedTransactionTypeFromStore ? !!this.selectedTransactionTypeFromStore.required_post_code : false;
    },
    isFilialRequired() {
      return true;
    }
  },
  methods: {
    formatNumber(number) {
      return String(number).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    },

    getCurrencyLabel() {
      this.$store.dispatch("personalEmployee/getCurrencyLabel").then((result) => {
        this.currencyLabel = result;
      });
    },

    onScroll(scrollTop) {
      this.scrolled = scrollTop > 150;
    },

    async open(data, params = {}) {
      this.isLoadingData = true;

      this.displayModal = true;

      //

      this.worker.id = this.$route.params.id;

      await this.$store.dispatch("personalEmployee/transactions/fetchTransactionTypesForLs", this.transactionTypes.selectedType);

      // await this.$store.dispatch("personalEmployee/transactions/fetchFilialsList").then(data => {
      //   this.filials.allFilials = data
      //     .sort((a, b) => b.sort - a.sort)
      //     .map((filial) => ({
      //       value: filial.id,
      //       label: filial.name,
      //     }))
      // })

      // await this.$store.dispatch("personalEmployee/getPostList").then(res => {
      //   const data = res.response;
        
      //   this.posts.allPosts = data.map(post => ({
      //     value: post.id,
      //     label: post.name
      //   }))
      // })

      await this.$store.dispatch("personalEmployee/transactions/fetchTransactionReasons", this.transactionTypes.selectedType);

      setTimeout(() => {
        this.$refs.dateInput.setCurrentDateInputTypeDate();
      }, 100);

      await this.$store.dispatch("personalEmployee/transactions/fetchCurrentUserWorker");

      await this.$store.dispatch("personalEmployee/transactions/fetchWorkersList");

      await this.$store.dispatch("personalEmployee/transactions/fetchEmployeeInfo", this.worker.id);

      await this.$store.dispatch("personalEmployee/transactions/fetchCompanies");

      await this.$store.dispatch("personalEmployee/getUserInfo", this.worker.id).then(res => {
        const data = res.response;
        
        this.worker.filial = data.filial;
        this.worker.filial_id = data.filial_id;
        this.worker.postCodes = data.postCodes;

        // Filials
        this.filials.allFilials = this.filials.allFilials.filter(filial => Number(filial.value) !== this.worker.filial_id);
        this.filials.allFilials.unshift({
          value: this.worker.filial_id, 
          label: this.worker.filial, 
          formatLabel: `<div style='font-weight: 500; border-bottom: 1px solid #DFE4EF;padding-bottom: 10px;'>${this.worker.filial}</div>`,
        });
        this.filials.selectedFilial = this.filials.allFilials[0];

        // Posts
        this.posts.allPosts = this.worker.postCodes.map(post => ({
          value: post.post_code,
          label: post.name
        }));

        this.posts.selectedPost = this.posts.allPosts[0];
      });

      await this.$store.dispatch("financialTransactions/getWorkerBalances", this.worker.id).then(res => {
        this.balances = res;
      });

      await this.$store.dispatch("financialTransactions/getFilialsWithWorkerBalances", this.worker.id).then(res => {
        this.filials.allFilials = res
          .sort((a, b) => b.sort - a.sort)
          .map(filial => ({
            ...filial,
            label: filial.name,
            value: filial.id,
            formatLabel: filial.amount !== null ? `<div style="display: flex; align-items: center; justify-content: space-between;"><span>${filial.name}</span><span style='white-space: nowrap; color:${filial.amount > 0 ? '#12C971;' : filial.amount < 0 ? '#FF238D;' : '#8E9CBB;' }'>${filial.amount} ` + this.currencyLabel + `</span></div>` : filial.name
          }));
      });

      //

      if (params.focusOnDescription) {
        this.$nextTick(() => {
          const field = this.$el.querySelector(".description");
          if (field) field.querySelector("textarea").focus();
        });
      }

      if (data) {
        this.setData(data);
      }

      this.isLoadingData = false;
    },
    
    close() {
      this.blocked = false;
      this.displayModal = false;
      this.blocked = false;
      this.reset();
    },

    reset() {
      this.blocked = false;
      this.isSubmitted = false;
      this.blocked = false;
      this.transactionAmount = '';
      
      this.comment = '';
      this.$refs.comment2.setValue('');
      this.$refs.comment2.textValue = '';
      this.$refs.comment2.$refs.textareaValue.innerHTML = '';

      this.transactionDate.value = '';
      this.transactionDate.error = '';
      this.$refs.dateInput.clear();

      this.worker.id = 0;
      this.worker.filial = '';
      this.worker.filial_id = 0;
      this.worker.postCodes = [];

      this.filials.selectedFilial = '';
      this.filials.allFilials = [];
      
      this.posts.selectedPost = '';
      this.posts.allPosts = [];

      this.transactionTypes.selectedType = "withdraw";
      this.transactionTypes.items = [];
      this.transactionTypes.selectedItem = '';

      this.transactionReasons.items = [];
      this.transactionReasons.selectedReason = '';
      
      this.workersList.items = [];
      this.workersList.selectedWorker = '';

      this.companies.items = [];
      this.companies.selectedCompany = '';
    },

    getParams() {
      const params = {
        id_worker: Number(this.worker.id),
        id_user: Number(this.userId),
        type: this.transactionTypes.selectedType,
        transaction_type_id: this.transactionTypes.selectedItem ? Number(this.transactionTypes.selectedItem.value) : null,
        filial_id: this.filials.selectedFilial ? Number(this.filials.selectedFilial.value) : null,
        post_code: this.posts.selectedPost ? this.posts.selectedPost.value : null,
        id_reason: Number(this.transactionReasons.selectedReason.value),
        amount: Number(this.transactionAmount),
        date_user: this.transactionDate.value,
        id_worker_author: this.workersList.selectedWorker ? Number(this.workersList.selectedWorker.value) : '',
        company_id: Number(this.companies.selectedCompany.value),
        comment: this.comment,
      };

      return params;
    },

    validateForm() {
      this.isSubmitted = true;

      return !this.transactionTypesError && !this.filialError && !this.postError && !this.transactionReasonsError && !this.transactionAmountError && !this.transactionDateError && !this.transactionDate.error && !this.companiesError;
    },

    async submit() {
      if (!this.validateForm() || this.submitIsDisabled) {
        return;
      }
      this.blocked = true;

      const params = this.getParams();

      this.submitIsDisabled = true;

      await this.$store.dispatch("personalEmployee/transactions/fetchCreateTransaction", params);

      this.submitIsDisabled = false;
      
      this.close();
      setTimeout(()=>this.blocked=false, 1000);
      this.$emit("submitted");
    },

    formatTransactionTypes() {
      this.transactionTypes.items = this.transactionTypesForLsFromStore.map((item) => ({
        label: `${item.short_name } ${ item.name}`,
        formatLabel: `<span style='color: ${item.type === "withdraw" ? '#ff238d' : '#12c971'}'>${item.short_name}</span> ${item.name}`,
        resultLabel: `<span style='color: ${item.type === "withdraw" ? '#ff238d' : '#12c971'}'>${item.short_name}</span> ${item.name}`,
        subLabel: item.description,
        value: item.id,
        dropdown_name: item.dropdown_name,
        description: item.description || '',
        sort: item.sort
      }));

      this.transactionTypes.items = this.transactionTypes.items.sort((a, b) => Number(a.sort) - Number(b.sort));
    },

    formatTransactionReasons() {
      this.transactionReasons.items = this.transactionReasonsFromStore.map((item) => ({
        label: item.name,
        value: item.id,
        subLabel: item.description || ''
      }));
    },

    formatWorkersList() {
      
      this.workersList.items = this.workersListFromStore.map((item) => ({
        label: item.name,
        value: item.id,
        formatLabel: `
            <div style="display: flex; align-items: center;">
              <div style="width: 34px; height: 34px; border-radius: 50%; background-color: #8e9cbb; margin-right: 10px; overflow: hidden; flex: 0 0 34px">
                <img src="${item.photo ? item.photo : 'redesign/img/userpic.svg'}" style="width: 100%; height: 100%; object-fit: cover;" />
              </div>
              <div>
                <div style="">${item.name}</div>
                <div style="color: #8E9CBB; font-size: 13px;">${item.filial_name}</div>   
              </div>
            </div>
          `,
      }));

      if (this.currentUserWorker && this.currentUserWorker.id) {
        this.workersList.items = this.workersList.items.filter((worker) => worker.value !== this.currentUserWorker.id);

        this.workersList.items.unshift({
          label: this.currentUserWorker.name,
          value: this.currentUserWorker.id,
          formatLabel: `
              <div style="display: flex; align-items: center; border-bottom: 1px solid #DFE4EF;padding-bottom: 10px;">
                <div style="width: 34px; height: 34px; border-radius: 50%; background-color: #8e9cbb; margin-right: 10px; overflow: hidden; flex: 0 0 34px">
                  <img src="${this.currentUserWorker.photo ? this.currentUserWorker.photo : 'redesign/img/userpic.svg'}" style="width: 100%; height: 100%; object-fit: cover;" />
                </div>
                <div>
                  <div style="font-weight: 500;">${this.currentUserWorker.name}</div>
                  <div style="color: #8E9CBB; font-size: 13px;">${this.currentUserWorker.filial_name}</div>   
                </div>
              </div>
            `,
        });

        this.workersList.selectedWorker = this.workersList.items[0];
      }
    },

    formatCompanies() {
      this.companies.items = [];

      Object.entries(this.companiesFromStore).forEach(([key, value]) => {
        this.companies.items.push({
          value: key,
          label: value
        });
      });

      if (this.employeeInfo.company_id) {
        const employeeCompany = this.companies.items.find(company => Number(company.value) === this.employeeInfo.company_id);
        this.companies.items = this.companies.items.filter(company => Number(company.value) !== this.employeeInfo.company_id);
        this.companies.items.unshift({
          value: employeeCompany.value,
          label: employeeCompany.label,
          formatLabel: `<div style='font-weight: 500; border-bottom: 1px solid #DFE4EF;padding-bottom: 10px;'>${employeeCompany.label}</div>`,
        });
        this.companies.selectedCompany = this.companies.items[0];
      } else if (this.employeeInfo.filial && this.employeeInfo.filial.company_id) {
        const filialCompany = this.companies.items.find(company => Number(company.value) === this.employeeInfo.filial.company_id);
        this.companies.items = this.companies.items.filter(company => Number(company.value) !== this.employeeInfo.filial.company_id);
        this.companies.items.unshift({
          value: filialCompany.value,
          label: filialCompany.label,
          formatLabel: `<div style='font-weight: 500; border-bottom: 1px solid #DFE4EF;padding-bottom: 10px;'>${filialCompany.label}</div>`,
        });
        this.companies.selectedCompany = this.companies.items[0];
      }
    },

    sendErrorDateMessage(message) {
      this.transactionDate.error = message;
    }
  },
  watch: {
    comment() {
      console.log(this.comment);
    },
    "transactionTypes.selectedType": function() {
      this.transactionTypes.items = [];
      this.transactionTypes.selectedItem = '';
      this.transactionReasons.items = [];
      this.transactionReasons.selectedReason = '';
      
      this.$store.dispatch("personalEmployee/transactions/fetchTransactionTypesForLs", this.transactionTypes.selectedType);
      this.$store.dispatch("personalEmployee/transactions/fetchTransactionReasons", this.transactionTypes.selectedType);
    }, 
    transactionTypesForLsFromStore() {
      this.formatTransactionTypes();
    },
    transactionReasonsFromStore() {
      this.formatTransactionReasons();
    },
    workersListFromStore() {
      this.formatWorkersList();
    },
    companiesFromStore() {
      this.formatCompanies();
    },
    "transactionTypes.selectedItem": function(value) {
      console.log(value);
      console.log(this.allTransactionTypesFromStore);
      this.selectedTransactionTypeFromStore = this.allTransactionTypesFromStore.find(el => el.id === value.value);
      console.log('this.selectedTransactionTypeFromStore',this.selectedTransactionTypeFromStore);

      console.log('1',this.isPostCodeRequired);
      console.log('2',this.isFilialRequired);
      // this.transactionReasons.selectedReason = "";
      // this.moneySourcesList.selectedMoneySource = "";
      // if (!this.transactionTypes.selectedItem) {
      //   this.formatTransactionReasons();
      // } else if (this.transactionTypes.selectedItem.short_name === "ШТРФ" || this.transactionTypes.selectedItem.short_name === "ПРЕМ") {
      //   // await this.$store.dispatch("finesAndBonuses/fetchFinesAndBonuses", this.posts.selectedPost ? this.posts.selectedPost.value : "");
      //   if (this.transactionTypes.selectedItem.short_name === "ШТРФ") {
      //     this.transactionReasons.items = this.finesCollection.items;
      //     this.transactionReasons.groups = this.finesCollection.groups;
      //   }
      //   if (this.transactionTypes.selectedItem.short_name === "ПРЕМ") {
      //     this.transactionReasons.items = this.bonusesCollection.items;
      //     this.transactionReasons.groups = this.bonusesCollection.groups;
      //   }
      // } else if (this.transactionTypes.selectedItem.short_name === "ОТП" && this.workersList.selectedEmployee) {
      //   this.getVacationSickList(this.workersList.selectedEmployee.value, "vacation");
      // } else if (this.transactionTypes.selectedItem.short_name === "БЛН" && this.workersList.selectedEmployee) {
      //   this.getVacationSickList(this.workersList.selectedEmployee.value, "sick");
      // } else if (this.transactionTypes.selectedItem.reasons) {
      //   this.transactionReasons.items = Object.values(this.transactionTypes.selectedItem.reasons).map(reason => ({
      //     value: reason.id,
      //     label: reason.name,
      //     subLabel: reason.description,
      //   }));
      // } else {
      //   this.formatTransactionReasons();
      // }
    },
  }
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/vars.scss";
@import "~@/assets/styles/grid.scss";

::v-deep {
  .sidesheet {
    &__header {
      display: none;
    }
    &__wrapper {
      padding: 0 !important;
      z-index: 1001;
    }

    &__content {
      height: 100%;
      padding: 0;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    &__close {
      padding-right: 10px;
      padding-top: 10px;
    }
  }
}

.modal-frame {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  /deep/ {
    .c-select-required {
      position: static;
      margin-top: 5px;
    }

    .c-select__input.c-select__input_required {
      &:focus {
        border-color: #ff238d;
      }
    }

    .scroll-container {
      padding: 35px 30px;

      @include vp-767 {
        padding: 30px 20px;
      }
    }
  }
  
  .h-switcher {
    /deep/ {
      .c-switcher-group {
        display: flex;
      }
    }
  }

  .modal-wrapper {
    flex-grow: 1;
    display: flex;
    overflow: hidden;
  }

  .modal-track {
    width: 100%;
    height: 100%;

    /deep/ {
      .scroll-container {
        padding-right: 0;
      }
    }
  }

  .modal-actions {
    flex-shrink: 0;
    background: white;
    display: none;

    & button {
      flex-basis: 100%;
      width: 100%;
      border-radius: 0;
      flex-shrink: 1;
    }

    @include vp-767 {
      display: flex;
    }
  }

  .form-group {
    max-width: 564px;
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    margin-bottom: 30px;

    &__column {
      width: 100%;
      position: relative;

      &--2 {
        width: calc((100% - 25px) / 2);
        
        &:not(:last-child) {
          margin-right: 25px;
        }

        @include vp-767 {
          width: 100%;

          &:not(:last-child) {
            margin-right: 0;
            margin-bottom: 30px;
          }
        }
      }

      &--3 {
        width: calc((100% - 25px * 2) / 3);
        
        &:not(:last-child) {
          margin-right: 25px;
        }

        @include vp-767 {
          width: calc((100% - 16px) / 2);

          &:not(:last-child) {
            margin-right: 16px;
          }
        }
      }
    }

    &__item-description {
      position: absolute;
      top: 50%;
      right: -40px;
      transform: translateY(-50%);

      &.c-button_icon_info-new {
        border: unset;
        padding: 0;
        font-size: 13px;
        color: $color-primary;
        align-items: center;

        /deep/ {
          .c-button-label {
            margin-top: 4px;
          }
        }
      }

      &--mobile {
        display: none;
      }

      &--desktop {
        margin: 4px 0 0;

        /deep/ {
          .c-button-icon {
            transform: translate(-50%, -50%);
          }
        }
      }

      @include vp-767 {
        position: relative;
        transform: unset;
        top: unset;
        right: unset;
        margin-top: 15px;

        &--desktop {
          display: none;
        }

        &--mobile {
          display: flex;
          height: fit-content;
        }
      }
    }
  }

  &__header {
    margin-bottom: 30px;
  }

  &__title {
    margin-bottom: 12px;
    font-size: 18px;
    font-weight: 500;
  }

  &__actions {
    margin-bottom: 12px;
    display: flex;
    align-items: center;

    @include vp-767 {
      display: none;
    }
  }

  &__action {
    &:not(:last-child) {
      margin-right: 10px;
    }
  }

  @include bootstrapMd {
    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    &__title {
      font-size: 22px;
      margin-bottom: 0;
    }
    &__actions {
      margin-bottom: 0;
    }
  }
}

/deep/ {
  .c-input-required_active {
    position: static;
    margin-top: 5px;
  }

  .loader-overlay {
    border: unset;
  }
}

// .violation-type {
//   &__wrapper {
//     padding: 30px 20px 20px;
//   }

//   &__category {
//     padding-top: 6px;
//   }

//   .is-invalid {
//     padding-bottom: 20px;
//   }

//   &__actions {
//     display: none;
//     flex-shrink: 0;
//   }
// }

// @include bootstrapMd {
//   .violation-type {
//     @include gridGenerator(11);

//     padding-left: 30px;
//     padding-right: 87px;
//     padding-bottom: 40px;
//     padding-top: 25px;

//     &__importance {
//       padding-top: 14px;
//     }

//     &__wrapper {
//       padding: 0 0 40px;
//     }

//     &__actions {
//       display: flex;
//       & button {
//         margin-right: 10px;
//       }
//     }
//   }
//   .modal-frame {
//     .modal-actions {
//       display: none;
//     }
//   }
// }

// .modal-frame__subtitle {
//   font-size: 15px;
// }

// .form-group {
//   max-width: 564px;
// }
// .modal-frame__header {
//   margin-bottom: 20px;
// }
// .modal-frame__subtitle {
//   margin-bottom: 30px;
// }
// .modal-frame__subtitle-short-name {
//   &--withdraw {
//     color: $pink;
//   }

//   &--deposit {
//     color: $color-success;
//   }
// }

// .modal-frame__conditions-title {
//   font-weight: 500;
//   font-size: 18px;
//   line-height: 22px;
// }
// .modal-frame__conditions-list {
//   list-style: none;
//   padding: 0;
//   margin-top: 25px;
// }
// .modal-frame__conditions-item {
//   display: grid;
//   grid-template-columns: 1fr 1fr auto;
//   gap: 30px;
//   align-items: center;

//   &:not(:last-child) {
//     margin-bottom: 30px;
//   }

//   @include vp-767 {
//     grid-template-columns: auto;
//   }
// }
// .modal-frame__conditions-item-remove {
//   opacity: 0;
//   pointer-events: none;
//   transition: 0.3s ease-in-out;

//   &.isActive {
//     opacity: 1;
//     pointer-events: all;
//   }

//   @include vp-767 {
//     display: none;
//   }
// }

// .modal-frame__conditions-item-header {
//   display: none;

//   @include vp-767 {
//     display: flex;
//     align-items: center;
//     justify-content: space-between;
//     font-weight: 500;
//     font-size: 15px;

//     .modal-frame__conditions-item-remove {
//       display: flex;
//     }
//   }
// }

.form-group__item-balances {
  display: flex;
}
.form-group__item-balances-item {
  &:not(:last-child) {
    margin-right: 20px;
  }
}
.form-group__item-balances-item-header {
  display: flex;
  align-items: center;
}
.form-group__item-balances-item-number {
  color: #8E9CBB;
  font-size: 18px;
  font-weight: 500;
  line-height: normal;

  &.positive {
    color: #12C971;
  }
  &.negative {
    color: #FF238D;
  }
}
.form-group__item-balances-item-name {
  font-size: 13px;
  line-height: 110%;
  margin-top: 5px;
}
.form-group__balances-description {
  margin-left: 10px;
  /deep/ {
    .c-button-icon {
      transform: translate(-50%, -50%);
    }
  }
}

/deep/ {
  .tablet {
    display: none;

    @include vp-1023 {
      cursor: pointer;
      background: none;
      border: 1px solid #d3d8df;
      height: 38px;
      border-radius: 19px;
      padding: 0 30px;
      color: #000;
      white-space: nowrap;
      -webkit-transition: 0.2s;
      transition: 0.2s;
      display: -webkit-box;
      display: -ms-flexbox;
      display: flex;
      -ms-flex-wrap: nowrap;
      flex-wrap: nowrap;
      -webkit-box-align: center;
      -ms-flex-align: center;
      align-items: center;
      -webkit-box-pack: center;
      -ms-flex-pack: center;
      justify-content: center;
      font-size: 15px;
      font-weight: 500;
      -webkit-box-sizing: border-box;
      box-sizing: border-box;
      -ms-flex-negative: 0;
      flex-shrink: 0;
      position: relative;
      margin: 0 auto;
    }
  }
}
</style>
